import { create } from 'zustand';
import { persist, type PersistStorage } from 'zustand/middleware';
import { LocalStorage } from 'services/storage/LocalStorage';
import { LocalStorageKeyFactory } from 'services/storage/keys/LocalStorageKeyFactory';
import { createSelectors, StoreName, withDevtools } from 'store/config';
import {
  createInitialUiState,
  parsePersistedUiState,
  setFormEditing,
  UI_PERSIST_VERSION,
} from './model';
import type { PersistedUiState, UiStore } from './types';

const uiPersistStorage: PersistStorage<PersistedUiState> = {
  getItem: () =>
    parsePersistedUiState(
      LocalStorage.getSilent<unknown>(
        LocalStorageKeyFactory.PERSIST_UI_KEY,
        null
      )
    ),
  setItem: (_name, value) =>
    LocalStorage.set(LocalStorageKeyFactory.PERSIST_UI_KEY, value),
  removeItem: () => LocalStorage.delete(LocalStorageKeyFactory.PERSIST_UI_KEY),
};

const useUiStoreBase = create<UiStore>()(
  withDevtools(
    persist(
      (set) => ({
        data: createInitialUiState(),

        actions: {
          setLocale: (currentLocale) =>
            set((state) => ({ data: { ...state.data, currentLocale } })),

          setFormEditing: (form, isFormEditing) =>
            set((state) => {
              const data = setFormEditing(state.data, form, isFormEditing);
              return data === state.data ? state : { data };
            }),

          clearFormsEditing: () =>
            set((state) => ({ data: { ...state.data, isFormEditing: {} } })),

          markVersionMismatch: () =>
            set((state) =>
              state.data.mismatchedVersion
                ? state
                : { data: { ...state.data, mismatchedVersion: true } }
            ),

          reset: () => set({ data: createInitialUiState() }),
        },
      }),
      {
        name: LocalStorageKeyFactory.PERSIST_UI_KEY,
        version: UI_PERSIST_VERSION,
        storage: uiPersistStorage,
        partialize: (state) => ({ currentLocale: state.data.currentLocale }),
        merge: (persisted, current) => ({
          ...current,
          data: { ...current.data, ...(persisted as PersistedUiState) },
        }),
      }
    ),
    StoreName.Ui
  )
);

export const useUiStore = createSelectors(useUiStoreBase);

export const getUiStoreState = (): UiStore => useUiStore.getState();
